


































































import {
  computed,
  defineComponent,
  PropType,
  toRefs
} from '~/utils/nuxt3-migration'
import { ClassifiedOfferEntry, OfferState } from '~/models/classified/offers'
import { formatNumber } from '~/utils/number'
import CPrice from '~/components/shared/configurable/CPrice.vue'
import { useI18n } from '~/compositions/i18n'
import { useNamespacedStore } from '~/compositions/store'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import { faCheckCircle, faTimes } from '@fortawesome/free-solid-svg-icons'

export default defineComponent({
  components: { CPrice },
  props: {
    inline: {
      type: Boolean,
      required: false,
      default: false
    },
    offer: {
      type: Object as PropType<ClassifiedOfferEntry>,
      required: true
    },
    fullText: {
      type: Boolean,
      required: false,
      default: true
    },
    showDiff: {
      type: Boolean,
      required: false,
      default: true
    },
    colorize: {
      type: Boolean,
      required: false,
      default: true
    },
    showBox: {
      type: Boolean,
      required: false,
      default: true
    },
    showIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    disableFontWeights: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props) {
    const { offer, fullText } = toRefs(props)
    const { t } = useI18n()

    const latestBid = computed(() => {
      return offer.value.latestBid
    })
    const offerPending = computed(() => {
      return latestBid.value.state === OfferState.PENDING
    })
    const offerRejected = computed(() => {
      return latestBid.value.state === OfferState.REJECTED
    })
    const offerExpired = computed(() => {
      return latestBid.value.state === OfferState.EXPIRED
    })
    const offerAccepted = computed(() => {
      return latestBid.value.state === OfferState.ACCEPTED
    })
    const offerCancelled = computed(() => {
      return latestBid.value.state === OfferState.WITHDRAWN
    })
    const offerRejectedOrCancelled = computed(() => {
      return offerRejected.value || offerCancelled.value
    })

    const received = computed(() => {
      return !userGetters('userIds').includes(latestBid.value.user.id)
    })

    const isCounteroffer = computed(() => {
      return offerPending.value && offer.value.bidCount > 1
    })

    const formattedOfferAmount = computed(() => {
      return formatNumber(latestBid.value.amount) + ' €'
    })

    const startingPrice = computed(() => {
      return (
        offer.value.classified.priceParked ||
        offer.value.classified.rawPrice ||
        0
      )
    })

    const priceDiff = computed(() => {
      return startingPrice.value - latestBid.value.amount
    })
    const priceDiffFormatted = computed(() => {
      return formatNumber(Math.abs(parseFloat(priceDiff.value.toFixed(2))))
    })

    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)

    const introText = computed(() => {
      if (fullText.value) {
        if (offerAccepted.value) {
          return t('accepted offer')
        } else if (offer.value.bidCount > 1) {
          return received.value
            ? t('you received a counteroffer')
            : t('you made a counteroffer')
        } else if (offerPending.value) {
          return received.value
            ? t('you received an offer')
            : t('you made an offer')
        }
      } else if (offerAccepted.value) {
        return t('accepted offer')
      } else if (offer.value.bidCount > 1) {
        return t('counteroffer')
      } else if (received.value) {
        return t('offer::prosfora')
      } else if (offerPending.value) {
        return t('new offer')
      }
      return t('offer::prosfora')
    })

    const statusIcon = computed(() => {
      if (offerAccepted.value) {
        return {
          icon: faCheckCircle,
          color: 'tw-text-green-600',
          title: t('the offer was accepted')
        }
      } else if (offerRejected.value) {
        return {
          icon: faTimes,
          color: 'tw-text-red-600',
          title: t('the offer was rejected')
        }
      } else if (offerCancelled.value) {
        return {
          icon: faTimes,
          color: 'tw-text-red-600',
          title: t('the offer was cancelled')
        }
      }
      return null
    })

    return {
      latestBid,
      offerPending,
      offerRejected,
      offerExpired,
      offerAccepted,
      offerCancelled,
      isCounteroffer,
      offerRejectedOrCancelled,
      formattedOfferAmount,
      priceDiff,
      priceDiffFormatted,
      introText,
      received,
      faCheckCircle,
      statusIcon,
      startingPrice
    }
  }
})
